import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Button, CircularProgress, Tooltip, IconButton, Typography, ToggleButtonGroup, ToggleButton, FormControl, InputLabel, Select, MenuItem, Checkbox,  InputAdornment, FormGroup, FormControlLabel, Paper, Grid, Stack, Popover, Badge, Switch, Tabs, Tab, Divider, Chip, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, List, ListItem, ListItemButton, ListItemText, ListItemSecondaryAction, Snackbar, Alert } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import MapContainer from './MapContainer';
import DetailsPanel from './DetailsPanel';
import { publicInstance } from '../axiosInstance';
import { Data, parseGpsInput, ServiceData, ConnectionType, getServiceMarkerColor, getServiceMarkerIcon } from './utils';
import SearchIcon from '@mui/icons-material/Search';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { useTheme } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useNavigate, useParams } from 'react-router-dom'; // Import useParams
import PowerOffIcon from '@mui/icons-material/PowerOff';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { differenceInDays, parseISO } from 'date-fns';
import { COUNTRY_COORDINATES, CountryKey } from './constants';
import TuneIcon from '@mui/icons-material/Tune';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import RouterIcon from '@mui/icons-material/Router';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { GB, PL, QA, AT, DK, GH, IN, FR } from 'country-flag-icons/react/3x2';
import { styled } from '@mui/material/styles';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { COLORS } from "../../helpers/theme";

import BlockIcon from '@mui/icons-material/Block';
import { 
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  FiberNew as FiberNewIcon,
  DoNotDisturbOn as ExcludeIcon,
  RemoveCircle as ExcludeNewIcon
} from '@mui/icons-material';

import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import { determineOperator } from '../InventoryPanel/utils';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const getZIndexForColor = (color: string): number => {
    switch (color) {
        case 'red': return 3;
        case 'yellow': return 2;
        case 'green': return 1;
        default: return 0;
    }
};

interface ServiceMapProps {
    isMobile: boolean;
}
const colors = COLORS.dark;
const FilterPopover: React.FC<{
    title: string;
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    children: React.ReactNode;
}> = ({ title, open, anchorEl, onClose, children }) => (
    <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
        }}
    >
        <Paper 
            sx={{ p: 2, minWidth: 300, maxWidth: 400 }}
            onMouseLeave={onClose}
        >
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                {title}
            </Typography>
            {children}
        </Paper>
    </Popover>
);

interface CountryOption {
    key: CountryKey;
    label: string;
    icon: React.ReactNode;
    coordinates: { lat: number; lng: number };
}

const COUNTRY_OPTIONS: CountryOption[] = [
    { 
        key: 'Poland', 
        label: 'Poland', 
        icon: <PL style={{ width: '24px', height: '16px' }}/>,
        coordinates: { lat: 52.0693, lng: 19.4803 }
    },
    { 
        key: 'Qatar', 
        label: 'Qatar', 
        icon: <QA style={{ width: '24px', height: '16px' }}/>,
        coordinates: { lat: 25.3548, lng: 51.1839 }
    },
    { 
        key: 'Austria', 
        label: 'Austria', 
        icon: <AT style={{ width: '24px', height: '16px' }}/>,
        coordinates: { lat: 47.5162, lng: 14.5501 }
    },
    { 
        key: 'Denmark', 
        label: 'Denmark', 
        icon: <DK style={{ width: '24px', height: '16px' }}/>,
        coordinates: { lat: 56.155278, lng: 10.198778 }
    },
    { 
        key: 'Ghana', 
        label: 'Ghana', 
        icon: <GH style={{ width: '24px', height: '16px' }}/>,
        coordinates: { lat: 8.0, lng: -1.0 }
    },
    { 
        key: 'India', 
        label: 'India', 
        icon: <IN style={{ width: '24px', height: '16px' }}/>,
        coordinates: { lat: 20.59, lng: 78.96 }
    },
    { 
        key: 'France', 
        label: 'France', 
        icon: <FR style={{ width: '24px', height: '16px' }}/>,
        coordinates: { lat: 46.22, lng: 2.21 }
    },
];

const AnimatedSwitch = styled(Switch)(({ theme }) => ({
    width: 62,
    height: 34,
    padding: 7,
    '& .MuiSwitch-switchBase': {
        margin: 1,
        padding: 0,
        transform: 'translateX(6px)',
        '&.Mui-checked': {
            transform: 'translateX(22px)',
            '& .MuiSwitch-thumb:before': {
                backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
                    theme.palette.getContrastText(theme.palette.primary.main),
                )}" d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>')`,
            },
            '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
            },
        },
    },
    '& .MuiSwitch-thumb': {
        backgroundColor: theme.palette.primary.main,
        width: 32,
        height: 32,
        '&:before': {
            content: "''",
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
                theme.palette.getContrastText(theme.palette.primary.main),
            )}" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/></svg>')`,
        },
    },
    '& .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
        borderRadius: 20 / 2,
    },
}));

// Add new interface for tab panel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Update constant for panel height - make it smaller
const FILTER_PANEL_HEIGHT = '10vh';

// Update CustomTabPanel component
function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
      style={{ 
        padding: '12px',
        height: FILTER_PANEL_HEIGHT,
      }}
    >
      {value === index && (
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'flex-start',
          maxWidth: '100%',
          pl: 2
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Dodaj nowy interfejs dla wyników wyszukiwania
interface LocationResult {
    formatted_address: string;
    geometry: {
        location: {
            lat: () => number;
            lng: () => number;
        };
        bounds?: {
            getNorthEast: () => { lat: () => number; lng: () => number };
            getSouthWest: () => { lat: () => number; lng: () => number };
        };
        viewport?: {
            getNorthEast: () => { lat: () => number; lng: () => number };
            getSouthWest: () => { lat: () => number; lng: () => number };
        };
    };
    address_components: {
        long_name: string;
        short_name: string;
        types: string[];
    }[];
}

const ServiceMap: React.FC<ServiceMapProps> = ({ isMobile }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { view, boxId } = useParams<{ view: string; boxId: string }>(); // Dodajemy boxId
    const [serviceData, setServiceData] = useState<ServiceData[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedService, setSelectedService] = useState<ServiceData | null>(null);
    const [searchGps, setSearchGps] = useState('');
    const [searchedLocation, setSearchedLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [connectionType, setConnectionType] = useState<ConnectionType>('vpna');
    const [modemTypes, setModemTypes] = useState<string[]>([]);
    const [selectedModemTypes, setSelectedModemTypes] = useState<string[]>([]);
    const [boxFilter, setBoxFilter] = useState<string>('');
    const [resetAnchorEl, setResetAnchorEl] = useState<null | HTMLElement>(null);
    const [colorFilters, setColorFilters] = useState<string[]>([]);
    const [showDisconnected, setShowDisconnected] = useState(false);
    const [excludeDisconnected, setExcludeDisconnected] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState<CountryKey>('Poland');
    const [openPopover, setOpenPopover] = useState<{
        name: 'basicFilters' | 'locationSearch' | 'modemFilters' | 'connectionFilters' | null;
        anchorEl: HTMLElement | null;
    }>({ name: null, anchorEl: null });
    const [showNewlyConnected, setShowNewlyConnected] = useState(false);
    const [excludeNewlyConnected, setExcludeNewlyConnected] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const isDark = theme.name === 'dark';
    const [citySearch, setCitySearch] = useState('');
    const [showSearchMarker, setShowSearchMarker] = useState(false);
    const [initialZoom, setInitialZoom] = useState<number>(6.8);
    const [locationResults, setLocationResults] = useState<LocationResult[]>([]);
    const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
	const [errorDialog, setErrorDialog] = useState<{ open: boolean; title?: string; message: string; examples?: string[] }>({ open: false, title: 'Informacja', message: '' });
	const [copiedExampleIndex, setCopiedExampleIndex] = useState<number | null>(null);
	const [copySnackbar, setCopySnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
	const exampleRefs = useRef<Array<HTMLInputElement | null>>([]);

	const copyExample = async (text: string, index: number) => {
		const fallbackCopy = () => {
			document.getSelection()?.removeAllRanges();
			const textArea = document.createElement('textarea');
			textArea.value = text;
			textArea.style.position = 'fixed';
			textArea.style.left = '-9999px';
			document.body.appendChild(textArea);
			textArea.focus();
			textArea.select();
			const ok = document.execCommand('copy');
			document.body.removeChild(textArea);
			document.getSelection()?.removeAllRanges();
			return ok;
		};

		try {
			if (navigator.clipboard && window.isSecureContext) {
				await navigator.clipboard.writeText(text);
			} else {
				const ok = fallbackCopy();
				if (!ok) throw new Error('execCommand returned false');
			}
			setCopiedExampleIndex(index);
			setTimeout(() => setCopiedExampleIndex(null), 1500);
			setCopySnackbar({ open: true, message: 'Skopiowano do schowka', severity: 'success' });
		} catch (e) {
			try {
				const ok = fallbackCopy();
				if (ok) {
					setCopiedExampleIndex(index);
					setTimeout(() => setCopiedExampleIndex(null), 1500);
					setCopySnackbar({ open: true, message: 'Skopiowano do schowka', severity: 'success' });
					return;
				}
			} catch {}
			const input = exampleRefs.current[index];
			if (input) {
				input.focus();
				input.select();
			}
			setCopySnackbar({ open: true, message: 'Nie udało się skopiować automatycznie. Zaznaczono tekst — użyj Ctrl+C / Cmd+C.', severity: 'error' });
		}
	};
    const [selectedOperators, setSelectedOperators] = useState<string[]>([]);
    const OPERATORS = ['Orange', 'Play', 'Plus', 'Unknown'];

    useEffect(() => {
        fetchServiceData();
    }, []);

    useEffect(() => {
        if (boxId && serviceData.length > 0) {
            const service = serviceData.find(s => s.box.toString() === boxId);
            if (service) {
                setSelectedService(service);
            }
        }
    }, [boxId, serviceData]);

    const fetchServiceData = async () => {
        setLoading(true);
        try {
            const response = await publicInstance.get('/view_dacapi_service_map');
            setServiceData(response.data);
            // Extract unique modem types
            const uniqueModemTypes = Array.from(new Set(response.data.map((service: ServiceData) => service.modem)));
            setModemTypes(uniqueModemTypes as string[]);
        } catch (error) {
            console.error('Error fetching service data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkerClick = (box: number) => {
        const service = serviceData.find(s => s.box === box);
        if (service) {
            setSelectedService(service);
            navigate(`/map/service/${box}`); // Zmiana ścieżki na nową strukturę
        }
    };

    const handleSearchGpsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchGps(event.target.value);
    };

    const handleSearchGps = async () => {
        if (/^\d+$/.test(searchGps)) {
            // If input is only digits, treat as RSPO
            try {
                const response = await publicInstance.get(`/view_dacapi_bts_search_near_installations?order=distance.asc&limit=1&rspo=eq.${searchGps}`);
                if (response.data && response.data.length > 0) {
                    const { nask_latitude, nask_longitude } = response.data[0];
                    setSearchedLocation({ lat: nask_latitude, lng: nask_longitude });
                    setShowSearchMarker(true); // Dodano
                    setInitialZoom(13); // Dodano - przybliżenie dla RSPO
					} else {
						setErrorDialog({ open: true, title: 'Informacja', message: 'Nie znaleziono lokalizacji dla podanego numeru RSPO.' });
					}
            } catch (error) {
                console.error('Error fetching RSPO location:', error);
					setErrorDialog({ open: true, title: 'Błąd', message: 'Błąd pobierania lokalizacji dla numeru RSPO.' });
            }
        } else {
            // Existing GPS parsing logic
            const parsedLocation = parseGpsInput(searchGps);
            if (parsedLocation) {
                setSearchedLocation(parsedLocation);
                setShowSearchMarker(true); // Dodano
                setInitialZoom(13); // Dodano - przybliżenie dla współrzędnych GPS
				} else {
					setErrorDialog({ 
						open: true, 
						title: 'Nieprawidłowy format', 
						message: 'Nieprawidłowe współrzędne GPS lub numer RSPO.\nSkorzystaj z jednego z poniższych formatów lub wpisz numer RSPO:',
						examples: ['52.2297, 21.0122', 'N: 52.2297, E: 21.0122'] 
					});
				}
        }
    };

    const handleResetSearch = () => {
        setSearchGps('');
        setSearchedLocation(null);
        setShowSearchMarker(false); // Reset marker visibility
    };

    const handleConnectionTypeChange = (
        event: React.MouseEvent<HTMLElement>,
        newConnectionType: ConnectionType | null,
    ) => {
        if (newConnectionType !== null) {
            setConnectionType(newConnectionType);
        }
    };

    const handleResetModemFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
        setResetAnchorEl(event.currentTarget);
        setSelectedModemTypes([]);
    };

    const handleCloseResetTooltip = () => {
        setResetAnchorEl(null);
    };

    const handleBoxFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setBoxFilter(event.target.value);
    };

    const handleColorFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const color = event.target.name;
        setColorFilters(prev =>
            event.target.checked
                ? [...prev, color]
                : prev.filter(c => c !== color)
        );
    };

    const isDeviceDisconnected = (service: ServiceData): boolean => {
        const TOLERANCE = 3; // Próg tolerancji w procentach

        const isColorIndicatingDisconnection =
            getServiceMarkerColor(service, 'vpna') === 'red' ||
            getServiceMarkerColor(service, 'vpna') === 'yellow';

        const areValuesVerySimilar = (): boolean => {
            const values = [service.total_vpna, service.total_vpnb, service.total_ping].map(Number);
            const max = Math.max(...values);
            const min = Math.min(...values);
            return (max - min) <= TOLERANCE;
        };

        const isTimestampOld = (): boolean => {
            if (!service.time_stamp) return false;
            const timestampDate = parseISO(service.time_stamp);
            const daysDifference = differenceInDays(new Date(), timestampDate);
            return daysDifference >= 2;
        };

        return isColorIndicatingDisconnection && areValuesVerySimilar() && isTimestampOld();
    };

    const isNewlyConnected = (service: ServiceData): boolean => {
        const TOLERANCE = 3; // Próg tolerancji w procentach

        const isColorIndicatingDisconnection =
            getServiceMarkerColor(service, 'vpna') === 'red' ||
            getServiceMarkerColor(service, 'vpna') === 'yellow';

        const areValuesVerySimilar = (): boolean => {
            const values = [service.total_vpna, service.total_vpnb, service.total_ping].map(Number);
            const max = Math.max(...values);
            const min = Math.min(...values);
            return (max - min) <= TOLERANCE;
        };

        const isTimestampOld = (): boolean => {
            if (!service.time_stamp) return false;
            const timestampDate = parseISO(service.time_stamp);
            const daysDifference = differenceInDays(new Date(), timestampDate);
            return daysDifference <= 1;
        };

        return isColorIndicatingDisconnection && areValuesVerySimilar() && isTimestampOld();
    };

    const filteredServiceData = serviceData.filter(service =>
        (selectedModemTypes.length === 0 || selectedModemTypes.includes(service.modem)) &&
        (boxFilter === '' || service.box.toString().includes(boxFilter)) &&
        (colorFilters.length === 0 || colorFilters.includes(getServiceMarkerColor(service, connectionType))) &&
        (!showDisconnected || isDeviceDisconnected(service)) &&
        (!excludeDisconnected || !isDeviceDisconnected(service)) &&
        (!showNewlyConnected || isNewlyConnected(service)) &&
        (!excludeNewlyConnected || !isNewlyConnected(service)) &&
        (selectedOperators.length === 0 || selectedOperators.includes(determineOperator(service.iccid)))
    );

    const handleCountryChange = (
        event: React.MouseEvent<HTMLElement>,
        newCountry: CountryKey | null,
    ) => {
        if (newCountry !== null) {
            setSearchedLocation(null);
            setCitySearch(''); // Wyczyść również pole wyszukiwania miasta
            setShowSearchMarker(false); // Wyłącz marker wyszukiwania
            setInitialZoom(6.8);
            setSelectedCountry(newCountry);
        }
    };

    // Funkcja pomocnicza do zamykania wszystkich popupów
    const closeAllPopovers = () => {
        setOpenPopover({ name: null, anchorEl: null });
    };

    // Zmodyfikowane handlery dla przycisków
    const handleBasicFiltersClick = (event: React.MouseEvent<HTMLElement>) => {
        if (openPopover.name === 'basicFilters') {
            setOpenPopover({ name: null, anchorEl: null });
        } else {
            setOpenPopover({ name: 'basicFilters', anchorEl: event.currentTarget });
        }
    };

    const handleLocationSearchClick = (event: React.MouseEvent<HTMLElement>) => {
        if (openPopover.name === 'locationSearch') {
            setOpenPopover({ name: null, anchorEl: null });
        } else {
            setOpenPopover({ name: 'locationSearch', anchorEl: event.currentTarget });
        }
    };

    const handleModemFiltersClick = (event: React.MouseEvent<HTMLElement>) => {
        if (openPopover.name === 'modemFilters') {
            setOpenPopover({ name: null, anchorEl: null });
        } else {
            setOpenPopover({ name: 'modemFilters', anchorEl: event.currentTarget });
        }
    };

    const handleConnectionFiltersClick = (event: React.MouseEvent<HTMLElement>) => {
        if (openPopover.name === 'connectionFilters') {
            setOpenPopover({ name: null, anchorEl: null });
        } else {
            setOpenPopover({ name: 'connectionFilters', anchorEl: event.currentTarget });
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    // Add new handlers
    const handleShowDisconnected = () => {
        if (showDisconnected) {
            setShowDisconnected(false);
        } else {
            setShowDisconnected(true);
            setShowNewlyConnected(false); // Uncheck the other option
        }
    };

    const handleShowNewlyConnected = () => {
        if (showNewlyConnected) {
            setShowNewlyConnected(false);
        } else {
            setShowNewlyConnected(true);
            setShowDisconnected(false); // Uncheck the other option
        }
    };

    // Zmodyfikuj funkcję handleCitySearch
    const handleCitySearch = async () => {
        if (!citySearch.trim()) return;

        try {
            const geocoder = new google.maps.Geocoder();
            const result = await geocoder.geocode({ address: citySearch });
            
            if (result.results && result.results.length > 0) {
                if (result.results.length === 1) {
                    // Jeśli jest tylko jeden wynik, używamy go bezpośrednio
                    handleLocationSelect(result.results[0]);
                } else {
                    // Jeśli jest więcej wyników, pokazujemy dialog wyboru
                    setLocationResults(result.results);
                    setIsLocationDialogOpen(true);
                }
			} else {
				setErrorDialog({ open: true, title: 'Informacja', message: 'Nie znaleziono lokalizacji.' });
			}
        } catch (error) {
            console.error('Geocoding error:', error);
			setErrorDialog({ open: true, title: 'Błąd', message: 'Błąd wyszukiwania lokalizacji.' });
        }
    };

    // Dodaj nową funkcję do obsługi wyboru lokalizacji
    const handleLocationSelect = (location: LocationResult) => {
        const bounds = location.geometry.bounds || location.geometry.viewport;
        
        if (bounds) {
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const latDiff = Math.abs(ne.lat() - sw.lat());
            const lngDiff = Math.abs(ne.lng() - sw.lng());
            const diagonal = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
            
            let zoom;
            if (diagonal > 1) zoom = 10;
            else if (diagonal > 0.5) zoom = 11;
            else if (diagonal > 0.2) zoom = 12;
            else if (diagonal > 0.1) zoom = 13;
            else if (diagonal > 0.05) zoom = 14;
            else zoom = 15;
            
            setSearchedLocation({ 
                lat: location.geometry.location.lat(), 
                lng: location.geometry.location.lng() 
            });
            setShowSearchMarker(false);
            setInitialZoom(zoom);
            setIsLocationDialogOpen(false);
            setCitySearch('');
        }
    };

    // Add helper function to count filters for each tab
    const getFilterCounts = () => ({
        basic: (boxFilter ? 1 : 0),
        location: (searchedLocation || citySearch ? 1 : 0),
        modem: selectedModemTypes.length,
        connection: [
            colorFilters.length > 0,
            showDisconnected,
            excludeDisconnected,
            showNewlyConnected,
            excludeNewlyConnected,
            connectionType !== 'vpna'
        ].filter(Boolean).length
    });

    const handleClearAllFilters = () => {
        setSelectedModemTypes([]);
        setBoxFilter('');
        setColorFilters([]);
        setShowDisconnected(false);
        setExcludeDisconnected(false);
        setShowNewlyConnected(false);
        setExcludeNewlyConnected(false);
        setConnectionType('vpna');
        setSelectedOperators([]);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1' }}>
            <Paper elevation={2}>
                <Box sx={{ 
                    display: 'flex',
                    borderBottom: 1,
                    borderColor: 'divider'
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center',
                        borderRight: 1,
                        borderColor: isDark ? 'rgba(255, 255, 255, .125)' : '#e0e0e0',
                        width: '140px',
                        height: '100%',
                        p: 2,
                        gap: 1,
                        bgcolor: isDark ? 'background.paper' : 'background.paper'
                    }}>
                        <Box sx={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5
                        }}>
                            <Typography 
                                variant="h4" 
                                color="primary"
                                sx={{ 
                                    fontWeight: 'bold',
                                    color: isDark ? '#64B5F6' : '#1976D2'
                                }}
                            >
                                {filteredServiceData.length}
                            </Typography>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: 'text.secondary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}
                            >
                                of {serviceData.length} devices
                            </Typography>
                        </Box>

                        <Tooltip title="Clear all filters">
                            <IconButton
                                size="small"
                                onClick={handleClearAllFilters}
                                sx={{ 
                                    alignSelf: 'center',
                                    color: isDark ? '#64B5F6' : '#1976D2',
                                    '&:hover': {
                                        bgcolor: isDark ? 'rgba(100, 181, 246, 0.08)' : 'rgba(25, 118, 210, 0.08)'
                                    }
                                }}
                            >
                                <FilterListOffIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Tabs 
                            value={activeTab} 
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ 
                                minHeight: '40px',
                                '& .MuiTab-root': {
                                    minHeight: '40px',
                                    py: 0.5,
                                    color: isDark ? '#64B5F6' : 'inherit',
                                    '& .MuiSvgIcon-root': {
                                        color: isDark ? '#64B5F6' : 'inherit'
                                    }
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: isDark ? '#64B5F6' : 'primary.main'
                                }
                            }}
                        >
                            <Tab 
                                label="Basic" 
                                icon={<TuneIcon />} 
                                iconPosition="start"
                            />
                            <Tab 
                                label="Location" 
                                icon={<LocationSearchingIcon />}
                                iconPosition="start"
                            />
                            <Tab 
                                label="Hardware"
                                icon={<RouterIcon />}
                                iconPosition="start"
                            />
                            <Tab 
                                label="Connection" 
                                icon={<SignalCellularAltIcon />}
                                iconPosition="start"
                            />
                        </Tabs>

                        <CustomTabPanel value={activeTab} index={0}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    gap: 0.5
                                }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Box ID
                                    </Typography>
                                    <TextField
                                        placeholder="Filter by Box ID"
                                        size="small"
                                        value={boxFilter}
                                        onChange={handleBoxFilterChange}
                                        sx={{ 
                                            width: '170px',
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <FilterListIcon sx={{ fontSize: 18, color: isDark ? '#64B5F6' : '#1976D2' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: 0.5 
                                }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Region
                                    </Typography>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 0.5,
                                        p: 0.5,
                                        borderRadius: 1,
                                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'
                                    }}>
                                        {COUNTRY_OPTIONS.map((country) => (
                                            <Tooltip key={country.key} title={country.label}>
                                                <IconButton
                                                    onClick={() => setSelectedCountry(country.key)}
                                                    size="small"
                                                    sx={{
                                                        p: 0.5,
                                                        bgcolor: selectedCountry === country.key ? 
                                                            (isDark ? 'rgba(100, 181, 246, 0.15)' : 'rgba(25, 118, 210, 0.1)') : 
                                                            'transparent',
                                                        '&:hover': {
                                                            bgcolor: selectedCountry === country.key ?
                                                                (isDark ? 'rgba(100, 181, 246, 0.25)' : 'rgba(25, 118, 210, 0.2)') :
                                                                (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)')
                                                        }
                                                    }}
                                                >
                                                    {country.icon}
                                                </IconButton>
                                            </Tooltip>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        </CustomTabPanel>

                        <CustomTabPanel value={activeTab} index={1}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    gap: 0.5
                                }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Search by City
                                    </Typography>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 1,
                                        p: 0.5,
                                        borderRadius: 1,
                                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'
                                    }}>
                                        <TextField
                                            placeholder="Enter city name"
                                            size="small"
                                            value={citySearch}
                                            onChange={(e) => setCitySearch(e.target.value)}
                                            sx={{ 
                                                width: '200px',
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'
                                                }
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LocationCityIcon sx={{ fontSize: 18, color: isDark ? '#64B5F6' : '#1976D2' }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleCitySearch();
                                                }
                                            }}
                                        />
                                        <Button 
                                            variant="contained" 
                                            size="small" 
                                            onClick={handleCitySearch}
                                            sx={{
                                                bgcolor: isDark ? '#64B5F6' : '#1976D2',
                                                '&:hover': {
                                                    bgcolor: isDark ? '#90CAF9' : '#1565C0'
                                                }
                                            }}
                                        >
                                            Search
                                        </Button>
                                    </Box>
                                </Box>

                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    gap: 0.5
                                }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Search by Coordinates
                                    </Typography>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 1,
                                        p: 0.5,
                                        borderRadius: 1,
                                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'
                                    }}>
                                        <TextField
                                            placeholder="GPS or RSPO"
                                            size="small"
                                            value={searchGps}
                                            onChange={handleSearchGpsChange}
                                            sx={{ 
                                                width: '200px',
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'
                                                }
                                            }}
                                        />
                                        <Button 
                                            variant="contained" 
                                            size="small" 
                                            onClick={handleSearchGps}
                                            sx={{
                                                bgcolor: isDark ? '#64B5F6' : '#1976D2',
                                                '&:hover': {
                                                    bgcolor: isDark ? '#90CAF9' : '#1565C0'
                                                }
                                            }}
                                        >
                                            Search
                                        </Button>
                                    </Box>
                                </Box>

                                <IconButton 
                                    size="small" 
                                    onClick={() => {
                                        handleResetSearch();
                                        setCitySearch('');
                                    }}
                                    sx={{ 
                                        alignSelf: 'flex-end',
                                        color: isDark ? '#64B5F6' : '#1976D2',
                                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                        '&:hover': {
                                            bgcolor: isDark ? 'rgba(100, 181, 246, 0.08)' : 'rgba(25, 118, 210, 0.08)'
                                        }
                                    }}
                                >
                                    <LocationOffIcon />
                                </IconButton>
                            </Box>
                        </CustomTabPanel>

                        <CustomTabPanel value={activeTab} index={2}>
                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Modem Types
                                    </Typography>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexWrap: 'wrap', 
                                        gap: 1,
                                        p: 1,
                                        borderRadius: 1,
                                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                        border: `1px solid ${colors.borders.primary}`
                                    }}>
                                        {modemTypes.map((modemType) => {
                                            const isSelected = selectedModemTypes.includes(modemType);
                                            return (
                                                <Box key={modemType} component="span" sx={{ position: 'relative', display: 'inline-flex' }}>
                                                    <Chip
                                                        label={modemType}
                                                        size="small"
                                                        clickable
                                                        variant="outlined"
                                                        onClick={() => {
                                                            setSelectedModemTypes(prev => 
                                                                prev.includes(modemType) 
                                                                    ? prev.filter(type => type !== modemType)
                                                                    : [...prev, modemType]
                                                            );
                                                        }}
                                                        aria-pressed={isSelected}
                                                        sx={{ 
                                                            fontWeight: isSelected ? 600 : 400,
                                                            borderColor: isSelected 
                                                                ? (theme as any).palette.primary.main 
                                                                : (theme as any).palette.borders.primary,
                                                            backgroundColor: isSelected 
                                                                ? ((theme as any).palette.action.selected || 'rgba(25,118,210,0.08)')
                                                                : 'transparent'
                                                        }}
                                                    />
                                                    
                                                </Box>
                                            );
                                        })}
                                        {selectedModemTypes.length > 0 && (
                                            <Tooltip title="Clear modem filters">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setSelectedModemTypes([])}
                                                    sx={{ 
                                                        color: isDark ? '#64B5F6' : '#1976D2',
                                                        p: 0.5,
                                                        '&:hover': {
                                                            bgcolor: isDark ? 'rgba(100, 181, 246, 0.08)' : 'rgba(25, 118, 210, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    <FilterListOffIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        GSM Operator
                                    </Typography>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexWrap: 'wrap', 
                                        gap: 1,
                                        p: 1,
                                        borderRadius: 1,
                                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                        border: `1px solid ${colors.borders.primary}`
                                    }}>
                                        {OPERATORS.map((operator) => {
                                            const isSelected = selectedOperators.includes(operator);
                                            return (
                                                <Box key={operator} component="span" sx={{ position: 'relative', display: 'inline-flex' }}>
                                                    <Chip
                                                        label={operator}
                                                        size="small"
                                                        clickable
                                                        variant="outlined"
                                                        onClick={() => {
                                                            setSelectedOperators(prev => 
                                                                prev.includes(operator) 
                                                                    ? prev.filter(op => op !== operator)
                                                                    : [...prev, operator]
                                                            );
                                                        }}
                                                        aria-pressed={isSelected}
                                                        sx={{
                                                            fontWeight: isSelected ? 600 : 400,
                                                            borderColor: isSelected 
                                                                ? (theme as any).palette.primary.main 
                                                                : (theme as any).palette.borders.primary,
                                                            backgroundColor: isSelected 
                                                                ? ((theme as any).palette.action.selected || 'rgba(25,118,210,0.08)')
                                                                : 'transparent',
                                                            ...(operator === 'Unknown' && !isSelected ? { opacity: isDark ? 0.7 : 0.8 } : {})
                                                        }}
                                                    />
                                                    
                                                </Box>
                                            );
                                        })}
                                        {selectedOperators.length > 0 && (
                                            <Tooltip title="Clear operator filters">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setSelectedOperators([])}
                                                    sx={{ 
                                                        color: isDark ? '#64B5F6' : '#1976D2',
                                                        p: 0.5,
                                                        '&:hover': {
                                                            bgcolor: isDark ? 'rgba(100, 181, 246, 0.08)' : 'rgba(25, 118, 210, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    <FilterListOffIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </CustomTabPanel>

                        <CustomTabPanel value={activeTab} index={3}>
                            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                                {/* Connection Type Section */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    bgcolor: isDark ? 'background.paper' : 'background.paper',
                                    borderRadius: 1,
                                    p: 0.5
                                }}>
                                    <Typography variant="caption" sx={{ mb: 0.5 }}>Connection</Typography>
                                    <ToggleButtonGroup
                                        value={connectionType}
                                        exclusive
                                        onChange={handleConnectionTypeChange}
                                        aria-label="connection type"
                                        sx={{
                                            border: `1px solid ${colors.borders.primary}`
                                        }}
                                      >
                                        <ToggleButton value="vpna" aria-label="gsm" sx={{
                                            border: `1px solid ${colors.borders.primary}`
                                        }}>
                                          GSM
                                        </ToggleButton>
                                        <ToggleButton value="vpnb" aria-label="wifi" sx={{
                                            border: `1px solid ${colors.borders.primary}`
                                        }}>
                                          WiFi
                                        </ToggleButton>
                                        <ToggleButton value="vpns" aria-label="s" sx={{
                                            border: `1px solid ${colors.borders.primary}`
                                        }}>
                                          S
                                        </ToggleButton>
                                      </ToggleButtonGroup>
                                </Box>
  
                                <Divider orientation="vertical" flexItem />
  
                                {/* Status Colors Section */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    bgcolor: isDark ? 'background.paper' : 'background.paper',
                                    borderRadius: 1,
                                    p: 0.5
                                }}>
                                    <Typography variant="caption" sx={{ mb: 0.5 }}>Status</Typography>
                                    <Stack direction="row" spacing={0.5}>
                                        {['red', 'yellow', 'green'].map((color) => (
                                            <Box
                                                key={color}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    px: 1,
                                                    py: 0.5,
                                                    
                                                    borderRadius: 1,
                                                    border: `1px solid ${colors.borders.primary}`,
                                                    bgcolor: 'background.drawer',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => handleColorFilterChange({ 
                                                    target: { 
                                                        checked: !colorFilters.includes(color),
                                                        name: color 
                                                    }
                                                } as any)}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        bgcolor: color
                                                    }}
                                                />
                                                <Typography 
                                                    variant="body2"
                                                    sx={{ 
                                                        color: colorFilters.includes(color) ? color : 'text.secondary',
                                                        fontWeight: colorFilters.includes(color) ? 'bold' : 'normal'
                                                    }}
                                                >
                                                    {color.charAt(0).toUpperCase() + color.slice(1)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>

                                {/* Connection State Section */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    bgcolor: isDark ? 'background.paper' : 'background.paper',
                                    borderRadius: 1,
                                    p: 0.5
                                }}>
                                    <Typography variant="caption" sx={{ mb: 0.5 }}>Connection State</Typography>
                                    <Stack direction="row" spacing={2}>
                                        <Stack direction="row" spacing={0.5}>
                                            <Tooltip title="Show Disconnected">
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        '&::after': showDisconnected ? {
                                                            content: '""',
                                                            position: 'absolute',
                                                            bottom: -2,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            width: 4,
                                                            height: 4,
                                                            bgcolor: 'primary.main',
                                                            borderRadius: '50%'
                                                        } : {}
                                                    }}
                                                >
                                                    <IconButton 
                                                        size="small"
                                                        color={showDisconnected ? "primary" : "default"}
                                                        onClick={handleShowDisconnected}
                                                        sx={{ 
                                                            p: 0.5,
                                                            bgcolor: showDisconnected && isDark ? 'grey.800' : 'transparent'
                                                        }}
                                                    >
                                                        <SignalCellularConnectedNoInternet0Bar />
                                                    </IconButton>
                                                </Box>
                                            </Tooltip>
                                            <Tooltip title="Exclude Disconnected">
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        '&::after': excludeDisconnected ? {
                                                            content: '""',
                                                            position: 'absolute',
                                                            bottom: -2,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            width: 4,
                                                            height: 4,
                                                            bgcolor: 'primary.main',
                                                            borderRadius: '50%'
                                                        } : {}
                                                    }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        color={excludeDisconnected ? "primary" : "default"}
                                                        onClick={() => setExcludeDisconnected(!excludeDisconnected)}
                                                        sx={{ 
                                                            p: 0.5,
                                                            bgcolor: excludeDisconnected && isDark ? 'grey.800' : 'transparent'
                                                        }}
                                                    >
                                                        <ExcludeIcon />
                                                    </IconButton>
                                                </Box>
                                            </Tooltip>
                                        </Stack>
                                        <Stack direction="row" spacing={0.5}>
                                            <Tooltip title="Show Newly Connected">
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        '&::after': showNewlyConnected ? {
                                                            content: '""',
                                                            position: 'absolute',
                                                            bottom: -2,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            width: 4,
                                                            height: 4,
                                                            bgcolor: 'primary.main',
                                                            borderRadius: '50%'
                                                        } : {}
                                                    }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        color={showNewlyConnected ? "primary" : "default"}
                                                        onClick={handleShowNewlyConnected}
                                                        sx={{ 
                                                            p: 0.5,
                                                            bgcolor: showNewlyConnected && isDark ? 'grey.800' : 'transparent'
                                                        }}
                                                    >
                                                        <FiberNewIcon />
                                                    </IconButton>
                                                </Box>
                                            </Tooltip>
                                            <Tooltip title="Exclude Newly Connected">
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        '&::after': excludeNewlyConnected ? {
                                                            content: '""',
                                                            position: 'absolute',
                                                            bottom: -2,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            width: 4,
                                                            height: 4,
                                                            bgcolor: 'primary.main',
                                                            borderRadius: '50%'
                                                        } : {}
                                                    }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        color={excludeNewlyConnected ? "primary" : "default"}
                                                        onClick={() => setExcludeNewlyConnected(!excludeNewlyConnected)}
                                                        sx={{ 
                                                            p: 0.5,
                                                            bgcolor: excludeNewlyConnected && isDark ? 'grey.800' : 'transparent'
                                                        }}
                                                    >
                                                        <ExcludeNewIcon />
                                                    </IconButton>
                                                </Box>
                                            </Tooltip>
                                        </Stack>
                                    </Stack>
                                </Box>
                            </Box>
                        </CustomTabPanel>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flex: '1' }}>
                <Box sx={{ flex: isMobile ? '1 1 auto' : '0 1 65%', height: isMobile ? '70vh' : '80vh' }}>
                    <MapContainer
                        data={filteredServiceData.map(service => {
                            const color = getServiceMarkerColor(service, connectionType);
                            const markerIcon = getServiceMarkerIcon(color);
                            return {
                                ...service,
                                markerIcon,
                                markerColor: color,
                                zIndex: getZIndexForColor(color),
                                isSelected: service.box.toString() === boxId
                            };
                        })}
                        filteredData={filteredServiceData.map(service => {
                            const color = getServiceMarkerColor(service, connectionType);
                            const markerIcon = getServiceMarkerIcon(color);
                            return {
                                ...service,
                                markerIcon,
                                markerColor: color,
                                zIndex: getZIndexForColor(color),
                                isSelected: service.box.toString() === boxId
                            };
                        })}
                        handleMarkerClick={handleMarkerClick}
                        searchedLocation={searchedLocation}
                        selectedCountry={selectedCountry}
                        initialZoom={initialZoom}
                        showSearchMarker={showSearchMarker}
                    />
                </Box>

                <Box sx={{ flex: isMobile ? '1 1 auto' : '0 1 35%', height: isMobile ? '60%' : '100%', mt: isMobile ? 2 : 0 }}>
                    <DetailsPanel
                        isMobile={isMobile}
                        selectedDevice={selectedService} // Pass the selected service
                    />
                </Box>
            </Box>

            <Dialog 
                open={isLocationDialogOpen} 
                onClose={() => setIsLocationDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Select Location</DialogTitle>
                <DialogContent>
                    <List>
                        {locationResults.map((location, index) => {
                            // Znajdź województwo i powiat
                            const voivodeship = location.address_components.find(
                                comp => comp.types.includes('administrative_area_level_1')
                            )?.long_name;
                            const county = location.address_components.find(
                                comp => comp.types.includes('administrative_area_level_2')
                            )?.long_name;

                            return (
                                <ListItemButton 
                                    key={index}
                                    onClick={() => handleLocationSelect(location)}
                                    sx={{
                                        '&:hover': {
                                            bgcolor: theme.palette.mode === 'dark' 
                                                ? 'rgba(255, 255, 255, 0.08)' 
                                                : 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary={location.formatted_address}
                                        secondary={`${voivodeship || ''} ${county ? `- ${county}` : ''}`}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                </DialogContent>
            </Dialog>

			<Dialog 
				open={errorDialog.open} 
				onClose={() => setErrorDialog(prev => ({ ...prev, open: false }))}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{errorDialog.title || 'Informacja'}</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ whiteSpace: 'pre-line' }}>
						{errorDialog.message}
					</DialogContentText>
					{errorDialog.examples && errorDialog.examples.length > 0 && (
						<Stack spacing={1.5} sx={{ mt: 1 }}>
							{errorDialog.examples.map((example, index) => (
								<TextField
									key={index}
									value={example}
									inputRef={(el) => exampleRefs.current[index] = el}
									fullWidth
									margin="dense"
									variant="outlined"
									InputProps={{
										readOnly: true,
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													aria-label="copy"
													onClick={() => copyExample(example, index)}
												>
													{copiedExampleIndex === index ? <CheckIcon /> : <ContentCopyIcon />}
												</IconButton>
											</InputAdornment>
										),
									}}
									onFocus={(e) => e.target instanceof HTMLInputElement && e.target.select()}
									onClick={(e) => {
										const input = e.target as HTMLInputElement;
										if (input && input.select) {
											input.select();
										}
									}}
								/>
							))}
						</Stack>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setErrorDialog(prev => ({ ...prev, open: false }))} variant="contained">OK</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={copySnackbar.open}
				autoHideDuration={1500}
				onClose={() => setCopySnackbar(prev => ({ ...prev, open: false }))}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert onClose={() => setCopySnackbar(prev => ({ ...prev, open: false }))} severity={copySnackbar.severity} sx={{ width: '100%' }}>
					{copySnackbar.message}
				</Alert>
			</Snackbar>
        </Box>
    );
};

export default ServiceMap;
